import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { SearchSelect } from './search-select.tsx';

export type StorageLocationInputProps = {
    className?: string;
    disabled?: boolean;
    /**
     * When set, the storage matching this id is hidden from the list and
     * replaced by a "From header" option whose value is the empty string.
     */
    headerStorageId?: string;
    onChange?: (id: string) => void;
    placeholder?: string;
    value?: string;
};

export function StorageLocationInput({
    className,
    disabled,
    headerStorageId,
    onChange,
    placeholder,
    value,
}: StorageLocationInputProps): ReactElement {
    const { t } = useTranslation('sales-draft');
    const { data, isLoading } = api.platoUser.writeBackOptionsHeader.getCustomerStorageLocations.useQuery({});

    const options = useMemo(() => {
        if (!data) {
            return;
        }
        const options = data.map((x) => ({ id: x.id, label: `${x.id}: ${x.name}` }));

        if (headerStorageId) {
            // Here we add a "from header" option to the top of the list, which
            // signals that the value for the line item should not be sent to
            // the ERP. This is _in addition_ to the option to select whatever
            // value is already selected for the header, in which case this
            // _will_ be sent explicitly.
            const headerValue = data.find((x) => x.id === headerStorageId);
            if (headerValue) {
                options.unshift({
                    id: '',
                    label: t('sales_draft__item_form__storage_from_header', headerValue),
                });
            }
        }
        return options;
    }, [data, headerStorageId, t]);

    return (
        <SearchSelect
            className={className}
            disabled={disabled}
            items={options}
            idSelector={(x) => x.id}
            isLoading={isLoading}
            onSelectedIdChange={onChange}
            placeholder={placeholder}
            renderItem={(x) => x.label}
            selectedId={value}
            textSelector={(x) => x.label}
        />
    );
}
